// React Query hooks for the print-templates module.
//
// 5-min staleTime per PRINT_TEMPLATE_PLAN.md §6.3 — templates change
// rarely, and a stale layout is less harmful than the constant
// refetch a 0 ms staleTime would produce while moving between
// settings sub-pages.
//
// Render is a mutation (not a query) — we never want a stale PDF
// served to a customer.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  getPrintTemplate,
  getPrintTemplatesList,
  renderPrintTemplate,
  revertPrintTemplate,
  upsertPrintTemplate,
  type RenderFormat,
} from '../../../../api/printTemplates';
import { queryKeys } from '../../../../api/queryKeys';
import type {
  PrintTemplateInfo,
  PrintTemplatesListResponse,
  PrintTemplateType,
  UpsertPrintTemplateInput,
} from '../../../../types/api';

const STALE_MS = 5 * 60 * 1000;

export function usePrintTemplatesList() {
  return useQuery<PrintTemplatesListResponse>({
    queryKey: queryKeys.printTemplates.list,
    queryFn: getPrintTemplatesList,
    staleTime: STALE_MS,
  });
}

export function usePrintTemplate(type: PrintTemplateType) {
  return useQuery<PrintTemplateInfo>({
    queryKey: queryKeys.printTemplates.detail(type),
    queryFn: () => getPrintTemplate(type),
    staleTime: STALE_MS,
  });
}

export function useUpsertPrintTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { type: PrintTemplateType; body: UpsertPrintTemplateInput }) =>
      upsertPrintTemplate(input.type, input.body),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: queryKeys.printTemplates.list });
      void qc.invalidateQueries({
        queryKey: queryKeys.printTemplates.detail(vars.type),
      });
    },
  });
}

export function useRevertPrintTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (type: PrintTemplateType) => revertPrintTemplate(type),
    onSuccess: (_data, type) => {
      void qc.invalidateQueries({ queryKey: queryKeys.printTemplates.list });
      void qc.invalidateQueries({
        queryKey: queryKeys.printTemplates.detail(type),
      });
    },
  });
}

export interface RenderResult {
  /** Populated when the request was made with `format='pdf'`. */
  blob: Blob | null;
  /** Populated when the request was made with `format='html'`. */
  html: string | null;
}

export interface RenderInput {
  type: PrintTemplateType;
  context: Record<string, unknown>;
  format: RenderFormat;
}

/**
 * Render mutation. Not cached — each call re-renders against the
 * current effective template (per plan §6.3). The mutation result
 * shape is `{ blob, html }` with exactly one populated based on
 * `format`; callers narrow on the format they requested.
 */
export function useRenderPrintTemplate() {
  return useMutation<RenderResult, Error, RenderInput>({
    mutationFn: async (input) => {
      const result = await renderPrintTemplate(input.type, input.context, input.format);
      if (input.format === 'pdf') {
        return { blob: result as Blob, html: null };
      }
      return { blob: null, html: result as string };
    },
  });
}
